// ============================================================================
// OBT F2-B — Publication çalışma alanı (snapshot + parklar + cevap özetleri)
// ============================================================================
// Bir publication açıldığında immutable snapshot ile Formen'in parkları ve
// kendi cevap özetleri GÜVENLİ biçimde PARALEL yüklenir. Cevap formu YALNIZ
// snapshot sorularından oluşur (campaign draft DEĞİL).
//
// Parklar ve response özetleri gerçek ID alanlarıyla eşleştirilir:
//   publication_park_id / supplemental_park_id → response_id + status.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPublicationSnapshot,
  listMyParks,
  listMyResponses,
} from './responseApi';
import { normalizeResponseError } from './responseErrors';

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

/** Park kaydını, kendi cevap özetiyle eşleştirir (gerçek ID alanlarıyla). */
function mergeParksWithResponses(parks, responses) {
  const byPubPark = new Map();
  const bySuppPark = new Map();
  (responses || []).forEach((r) => {
    if (r.publication_park_id) byPubPark.set(String(r.publication_park_id), r);
    if (r.supplemental_park_id) bySuppPark.set(String(r.supplemental_park_id), r);
  });
  return (parks || []).map((p) => {
    const resp =
      (p.publication_park_id && byPubPark.get(String(p.publication_park_id))) ||
      (p.supplemental_park_id && bySuppPark.get(String(p.supplemental_park_id))) ||
      null;
    return {
      ...p,
      response: resp,
      response_id: resp?.response_id || p.response_id || null,
      response_status: resp?.status || p.response_status || null,
      answer_count: resp?.answer_count ?? 0,
      photo_count: resp?.photo_count ?? 0,
      revision_count: resp?.revision_count ?? 0,
      submitted_at: resp?.submitted_at || null,
    };
  });
}

export function usePublicationWorkspace(publicationId, { enabled = true } = {}) {
  const [state, setState] = useState({
    loading: Boolean(publicationId) && enabled,
    error: null,
    snapshot: null,
    parks: [],
    responses: [],
  });
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!publicationId || !enabled) {
      setState({ loading: false, error: null, snapshot: null, parks: [], responses: [] });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const [snapshot, parks, responses] = await Promise.all([
        getPublicationSnapshot(publicationId, { signal: controller.signal }),
        listMyParks(publicationId, { signal: controller.signal }),
        listMyResponses(publicationId, { signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return;
      setState({ loading: false, error: null, snapshot, parks, responses });
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;
      setState({
        loading: false,
        error: normalizeResponseError(error),
        snapshot: null,
        parks: [],
        responses: [],
      });
    }
  }, [publicationId, enabled]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const mergedParks = useMemo(
    () => mergeParksWithResponses(state.parks, state.responses),
    [state.parks, state.responses]
  );

  // Snapshot sorularını display_order ile sırala (immutable kaynak).
  const questions = useMemo(() => {
    const qs = Array.isArray(state.snapshot?.questions) ? state.snapshot.questions : [];
    return qs
      .slice()
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [state.snapshot]);

  return {
    ...state,
    parks: mergedParks,
    questions,
    reload: load,
  };
}
